<template>
  <div id="animation-container" ref="container">
    <div id="chat-log">
      <!-- 使用者訊息 1 -->
      <div class="bubble user-bubble" :class="{ 'animate-in': step >= 1 }">
        <div class="user-bubble-content">
          <p>{{ t('components.home-more-features-ai-consultant.user-request') }}</p>
        </div>
      </div>

      <!-- AI 回覆 1 -->
      <div class="bubble ai-bubble" :class="{ 'animate-in': step >= 2 }">
        <div class="ai-bubble-content">
          <p>{{ t('components.home-more-features-ai-consultant.ai-question') }}</p>
        </div>
      </div>
      
      <!-- 使用者訊息 2 -->
      <div class="bubble user-bubble" :class="{ 'animate-in': step >= 3 }">
        <div class="user-bubble-content">
          <p>{{ t('components.home-more-features-ai-consultant.user-answer') }}</p>
        </div>
      </div>

      <!-- AI 回覆 2 (Designing) -->
      <div class="bubble ai-bubble" :class="{ 'animate-in': step >= 4 }">
        <div class="ai-bubble-content">
          <div class="typing-indicator" v-show="step === 4 && showTyping">
            <div class="typing-dot"></div>
            <div class="typing-dot"></div>
            <div class="typing-dot"></div>
          </div>
          <p class="ai-text" v-show="step >= 5">{{ t('components.home-more-features-ai-consultant.ai-designing') }}</p>
        </div>
      </div>

      <!-- AI 流程圖氣泡 -->
      <div id="workflow-bubble" class="bubble ai-bubble" :class="{ 'animate-in': step >= 6 }">
        <div class="workflow-bubble-content">
          <div id="workflow-area">
            <h2 class="workflow-title">{{ t('components.home-more-features-ai-consultant.workflow-title') }}</h2>
            <div class="workflow-nodes-container">
              <div class="node" :class="{ 'animate-in': step >= 7 }">
                <div class="node-content">
                  <p><span class="node-text-main">{{ t('components.home-more-features-ai-consultant.nodes.submit-request') }}</span><br><span class="node-text-sub">({{ t('components.home-more-features-ai-consultant.nodes.applicant') }})</span></p>
                </div>
              </div>
              <div class="arrow-shape" :class="{ 'animate-in': step >= 8 }"></div>
              <div class="node" :class="{ 'animate-in': step >= 8 }">
                <div class="node-content">
                  <p><span class="node-text-main">{{ t('components.home-more-features-ai-consultant.nodes.manager-approval') }}</span><br><span class="node-text-sub">({{ t('components.home-more-features-ai-consultant.nodes.direct-manager') }})</span></p>
                </div>
              </div>
              <div class="arrow-shape" :class="{ 'animate-in': step >= 9 }"></div>
              <div class="node" :class="{ 'animate-in': step >= 9 }">
                <div class="node-content">
                  <p><span class="node-text-main">{{ t('components.home-more-features-ai-consultant.nodes.finance-approval') }}</span><br><span class="node-text-sub">({{ t('components.home-more-features-ai-consultant.nodes.finance-dept') }})</span></p>
                </div>
              </div>
              <div class="arrow-shape" :class="{ 'animate-in': step >= 10 }"></div>
              <div class="node" :class="{ 'animate-in': step >= 10 }">
                <div class="node-content">
                  <p><span class="node-text-main">{{ t('components.home-more-features-ai-consultant.nodes.payment-processed') }}</span><br><span class="node-text-sub">({{ t('components.home-more-features-ai-consultant.nodes.cashier') }})</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- AI 回報 -->
      <div class="bubble ai-bubble" :class="{ 'animate-in': step >= 11 }">
        <div class="ai-bubble-content">
          <p>{{ t('components.home-more-features-ai-consultant.ai-completion') }}</p>
        </div>
      </div>
    </div>
    
    <!-- 重新播放按鈕 - 右下角圓形按鈕 -->
    <button 
      id="replay" 
      @click="playAnimation" 
      class="replay-btn"
      :class="{ 'visible': step >= 11 }"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
        <path d="M21 3v5h-5"/>
        <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
        <path d="M3 21v-5h5"/>
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const container = ref<HTMLElement>()
const step = ref(0)
const showTyping = ref(true)
const hasPlayed = ref(false)

let observer: IntersectionObserver | null = null
let animationTimeouts: NodeJS.Timeout[] = []

// 清理定時器
function clearTimeouts() {
  animationTimeouts.forEach(timeout => clearTimeout(timeout))
  animationTimeouts = []
}

// 播放動畫的主函數
function playAnimation() {
  clearTimeouts()
  step.value = 0
  showTyping.value = true
  hasPlayed.value = true
  
  if (container.value) {
    container.value.scrollTop = 0
  }
  
  // 依序顯示每個對話氣泡和流程節點
  const delays = [
    300,   // 第1個氣泡
    1000,  // 第2個氣泡  
    1600,  // 第3個氣泡
    2200,  // 第4個氣泡 (打字中)
    3700,  // 顯示AI文字，隱藏打字動畫
    4300,  // 第5個氣泡 (流程圖)
    4900,  // 第1個節點
    5400,  // 第2個節點+箭頭
    5900,  // 第3個節點+箭頭  
    6400,  // 第4個節點+箭頭
    7000   // 最後一個氣泡
  ]
  
  delays.forEach((delay, index) => {
    const timeout = setTimeout(() => {
      if (index === 4) {
        showTyping.value = false
      }
      step.value = index + 1
      
      // 自動滾動到當前元素
      setTimeout(() => {
        if (container.value) {
          const bubbles = container.value.querySelectorAll('.bubble')
          const currentBubble = bubbles[Math.min(index, bubbles.length - 1)]
          if (currentBubble) {
            const containerHeight = container.value.clientHeight
            const bubbleTop = (currentBubble as HTMLElement).offsetTop
            const bubbleHeight = (currentBubble as HTMLElement).offsetHeight
            const scrollTop = Math.max(0, bubbleTop - containerHeight / 2 + bubbleHeight / 2)
            container.value.scrollTo({ top: scrollTop, behavior: 'smooth' })
          }
        }
      }, 100)
    }, delay)
    
    animationTimeouts.push(timeout)
  })
}

onMounted(() => {
  if (typeof window === 'undefined') return
  
  // 使用 Intersection Observer 監聽滾動
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasPlayed.value) {
          setTimeout(() => {
            playAnimation()
          }, 300)
        }
      })
    },
    {
      threshold: 0.3,
      rootMargin: '-50px 0px'
    }
  )

  if (container.value) {
    observer.observe(container.value)
  }
})

onUnmounted(() => {
  clearTimeouts()
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* --- 主要動畫容器 --- */
#animation-container {
  position: relative;
  width: 100%;
  max-width: 56rem;
  height: 500px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #172032;
  border-radius: 1rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border: 1px solid #334155;
  overflow-y: auto;
  font-family: 'Inter', sans-serif;
}

/* 響應式設計 */
@media (max-width: 768px) {
  #animation-container {
    height: 400px;
    padding: 1rem;
    margin: 0 1rem;
    max-width: calc(100vw - 2rem);
  }
}

@media (max-width: 480px) {
  #animation-container {
    height: 350px;
    padding: 0.75rem;
    margin: 0 0.5rem;
    max-width: calc(100vw - 1rem);
  }
}

#animation-container::-webkit-scrollbar {
  width: 6px;
}
#animation-container::-webkit-scrollbar-track {
  background: transparent;
}
#animation-container::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 3px;
}

/* --- 對話紀錄與氣泡 --- */
#chat-log {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.bubble {
  display: flex;
  align-items: flex-start;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.bubble.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.user-bubble {
  justify-content: flex-end;
}

.ai-bubble {
  justify-content: flex-start;
}

.user-bubble-content,
.ai-bubble-content,
.workflow-bubble-content {
  max-width: 28rem;
  padding: 1rem;
  border-radius: 1rem;
  color: white;
}

/* 響應式氣泡寬度 */
@media (max-width: 768px) {
  .user-bubble-content,
  .ai-bubble-content,
  .workflow-bubble-content {
    max-width: 85%;
    padding: 0.875rem;
  }
}

@media (max-width: 480px) {
  .user-bubble-content,
  .ai-bubble-content,
  .workflow-bubble-content {
    max-width: 90%;
    padding: 0.75rem;
  }
}

.user-bubble-content {
  background-color: #4f46e5;
  border-bottom-right-radius: 0.5rem;
}

.ai-bubble-content {
  background-color: #334155;
  border-bottom-left-radius: 0.5rem;
}

.workflow-bubble-content {
  flex: 1 1 0%;
  background-color: #334155;
  border-bottom-left-radius: 0.5rem;
}

.bubble p {
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5rem;
  margin: 0;
}

@media (max-width: 480px) {
  .bubble p {
    font-size: 0.9rem;
    line-height: 1.4rem;
  }
}


/* --- 打字動畫 --- */
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  height: 1.5rem;
}

.typing-dot {
  width: 0.5rem;
  height: 0.5rem;
  background-color: #94a3b8;
  border-radius: 50%;
  animation: typing-bounce 1.2s infinite ease-in-out;
}

.typing-dot:nth-child(2) { animation-delay: 0.2s; }
.typing-dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes typing-bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

/* --- 流程圖區域 --- */
.workflow-title {
  font-size: 1.125rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1.5rem;
  color: #cbd5e1;
}

@media (max-width: 480px) {
  .workflow-title {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
}

.workflow-nodes-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding: 0.5rem 0;
}

/* 響應式流程圖 */
@media (max-width: 768px) {
  .workflow-nodes-container {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
}

.node {
  flex: 1 1 0%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 0.25rem;
  position: relative;
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.node.animate-in {
  opacity: 1;
  transform: scale(1);
}

@media (max-width: 768px) {
  .node {
    flex: none;
    width: 100%;
  }
}

.node-content {
  background-color: rgba(71, 85, 105, 0.7);
  border: 1px solid #64748b;
  border-radius: 0.5rem;
  padding: 0.75rem;
  width: 8rem;
  height: 5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

@media (max-width: 768px) {
  .node-content {
    width: 100%;
    max-width: 16rem;
    height: 4rem;
    margin: 0 auto;
  }
}

@media (max-width: 480px) {
  .node-content {
    padding: 0.5rem;
    height: 3.5rem;
  }
}

.node-content:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 12px -3px rgba(0,0,0,0.2);
}

.node-content p {
  margin: 0;
}

.node-text-main {
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: white;
}

.node-text-sub {
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #94a3b8;
}

@media (max-width: 480px) {
  .node-text-main {
    font-size: 0.8rem;
    line-height: 1.1rem;
  }
  
  .node-text-sub {
    font-size: 0.7rem;
    line-height: 0.9rem;
  }
}

/* --- 箭頭 --- */
.arrow-shape {
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-left: 10px solid #4f46e5;
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  margin: 0 0.5rem;
}

.arrow-shape.animate-in {
  opacity: 1;
  transform: scale(1);
}

/* 響應式箭頭 */
@media (max-width: 768px) {
  .arrow-shape {
    border-top: none;
    border-bottom: 10px solid #4f46e5;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    transform: scale(0.5) rotate(180deg);
    margin: 0;
    align-self: center;
  }
  
  .arrow-shape.animate-in {
    transform: scale(1) rotate(180deg);
  }
}

/* --- 重播按鈕 --- */
#replay {
  position: sticky;
  float: right;
  bottom: 1rem;
  right: 1rem;
  width: 3rem;
  height: 3rem;
  background-color: rgba(71, 85, 105, 0.9);
  color: #cbd5e1;
  border: 1px solid #475569;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  opacity: 0;
  visibility: hidden;
  backdrop-filter: blur(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 100;
  margin-top: 1rem;
  margin-left: auto;
}

#replay.visible {
  opacity: 1;
  visibility: visible;
}

@media (max-width: 480px) {
  #replay {
    width: 2.5rem;
    height: 2.5rem;
    bottom: 0.5rem;
    right: 0.5rem;
  }
}

#replay:hover {
  background-color: rgba(79, 70, 229, 0.9);
  color: white;
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
}

#replay svg {
  transition: transform 0.3s ease;
}

#replay:hover svg {
  transform: rotate(180deg);
}

@media (max-width: 480px) {
  #replay svg {
    width: 14px;
    height: 14px;
  }
}
</style>